// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
import { MediaPreviewComponent } from '@epicuro-next/ataraxia/media-preview';
import {
  recommendationsPanelsLocaleKeys,
  thankYouPageMarketingPanelHeading,
} from '@epicuro-next/constants/features/localization';
import {
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import { getIdProvider } from '../../../utils/dynamic-items-utils';
import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
  recommendationItemShownEventHandler,
} from '../../events';
import { errorMessage, ifIsLoading } from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import { getFilterProperties } from '../../search-filters';

import { getDataForBestPerformingArticles } from './best-performing-acticles-helpers';

const panelNameProvider: IDynamicBindableValue = {
  provider: 'id:checkout-root',
  property: 'panelName',
};

const panelStyleProvider: IDynamicBindableValue = {
  provider: 'id:checkout-root',
  property: 'panelTrackingId',
};

const loadingSkeletonItem: IDynamicItem = {
  id: 'skeleton-media-preview',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['h-100'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      url: 'assets/static-skeleton-screen.png',
    },
    objectFit: 'cover',
  },
};

const loadingSkeleton: IDynamicItem = {
  id: 'loading-skeleton',
  contentTypeId: 'dynamic-collections-renderer',
  if: ifIsLoading(),
  children: [
    {
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['col'],
      },
      children: [loadingSkeletonItem],
    },
  ],
};

const header: IDynamicSimpleText = {
  id: 'header',
  contentTypeId: 'dynamic-elements-simple-text',
  inputs: {
    text: thankYouPageMarketingPanelHeading,
  },
  styles: {
    classes: ['marketing-panel__heading'],
  },
};

const itemMediaPreview: IDynamicItem = {
  id: 'item-media-preview',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['marketing-panel__item'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      provider: 'self',
      property: 'productData',
      transform: function (productDataArg: {
        media: MediaPreviewComponent['media'];
      }) {
        return productDataArg.media;
      }.toString(),
    },
  },
  eventHandlers: {
    onClick: [
      recommendationItemClickedEventHandler(
        panelNameProvider,
        panelStyleProvider,
        { provider: 'self', property: 'productData' },
      ),
      openConfigStripEventHandler('self'),
    ],
    onVisibleOnce: [
      recommendationItemShownEventHandler(
        panelNameProvider,
        panelStyleProvider,
        { provider: 'self', property: 'productData' },
      ),
    ],
  },
};

const itemsArrayRenderer: IDynamicItem = {
  id: 'items-array-renderer',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: [
    'AND',
    [
      [
        'EQ',
        [
          {
            provider: 'parent',
            property: 'isLoading',
          },
          false,
        ],
      ],
      [
        'EQ',
        [
          {
            provider: 'parent',
            property: 'hasError',
          },
          false,
        ],
      ],
    ],
  ],
  data: {
    products: {
      provider: 'parent',
      property: 'productData',
      transform: function (productDataArg: []) {
        return (productDataArg ?? []).slice(0, 5);
      }.toString(),
    },
    loadingStateWrapper: {
      provider: 'parent',
      property: 'loadingStateWrapper',
    },
  },
  styles: {
    classes: ['marketing-panel__items'],
  },
  inputs: {
    propertyToUse: 'productData',
    items$: {
      provider: 'self',
      property: 'products',
    },
    contentType: itemMediaPreview,
  },
};

const textMessageClasses = [
  'marketing-panel__items',
  'd-flex',
  'w-100',
  'justify-content-left',
  'text-muted',
];

const noItemsMessage = conditionalMessage(
  'no-items-message',
  'parent',
  'hasNoItems',
  recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
  {
    classes: textMessageClasses,
  },
);

const errorMessageItem: IDynamicSimpleText = {
  ...errorMessage(),
  styles: {
    classes: textMessageClasses,
  },
};

const recommendedItems: IDynamicItem = {
  id: 'checkout-loading-container',
  contentTypeId: 'dynamic-collections-renderer',
  styles: {
    classes: ['col'],
  },
  if: [
    'EQ',
    [
      false,
      {
        provider: 'parent',
        property: 'isLoading',
      },
    ],
  ],
  children: [
    {
      id: 'checkout-done-loading-container',
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: [
          'marketing-panel',
          'panel-5-items-with-no-media',
          'd-flex',
          'h-100',
          'mb-2',
        ],
        style: {
          'border-radius': 'var(--border-radius-lg, .25rem)',
        },
      },
      children: [header, itemsArrayRenderer, errorMessageItem, noItemsMessage],
    },
  ],
};

export const checkout: IDynamicItem = {
  id: 'checkout-root',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    panelName: 'ThankYouPage',
    filterMissingImages: false,
    ...getFilterProperties(
      {
        provider: 'id:best-performing-articles-filter', // Does not actually exist as this panel has no UI for setting filters.
        property: 'filtersChanged',
        startWith: { value: undefined },
      },
      {
        initialCategories: [],
        initialDaysIntoThePast: 30,
      },
    ),
    ...getDataForBestPerformingArticles(
      {
        provider: 'self',
        property: 'panelName',
      },
      {
        provider: 'id:checkout-root',
        property: 'filters',
      },
      {
        page: 'ty',
        location: '',
        type: '5',
        provider: 'nebp',
        cssVariant: '001',
      },
      getIdProvider('checkout-root', 'filterMissingImages'),
    ),
  },
  children: [
    {
      id: 'checkout-container',
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['row', 'mx-2', 'mb-2'],
      },
      children: [loadingSkeleton, recommendedItems],
    },
  ],
};
